import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, Sparkles } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useRequireAuth } from "@/hooks/useRequireAuth";

// ── Report palette — matches backend/app/pdf_generator.py exactly ─────────
const IVORY = "#F7F4EC";
const FOREST = "#183E35";
const BODY_C = "#304039";
const MUTED = "#657267";
const BRASS = "#A4864B";
const PALE_BRASS = "#CBB480";
const SAGE = "#E6EADF";
const RULE = "#D8DCCF";

const F_SERIF = "'LinLibertine', 'Times New Roman', serif";
const F_SANS = "'Carlito', system-ui, sans-serif";

const LANGUAGES = [
  { code: "hinglish", label: "Hinglish" },
  { code: "english", label: "English" },
  { code: "hindi", label: "हिंदी" },
];

const TRANSLATE_QUOTES = [
  "The mind is not a vessel to be filled, but a chart to be read.",
  "Self-knowledge is the beginning of self-mastery.",
  "What you understand about yourself, you can no longer be ruled by.",
  "Every pattern you notice is a pattern you can change.",
  "Clarity is the first step toward change.",
  "You are not your thoughts - you are the one who notices them.",
];

// Mirrors backend/app/pdf_generator.py's parse_sections() — splits the AI's
// [[SECTION: Name]] markers into headed blocks so we can render real
// headings instead of the raw marker text.
function parseReportSections(text) {
  if (!text) return [];
  const markerRe = /^\[\[SECTION:\s*(.+?)\s*\]\]\s*$/gm;
  const matches = [...text.matchAll(markerRe)];
  if (matches.length === 0) return [{ heading: null, body: text.trim() }];

  const blocks = [];
  const intro = text.slice(0, matches[0].index).trim();
  if (intro) blocks.push({ heading: null, body: intro });

  matches.forEach((m, i) => {
    const heading = m[1].trim();
    const start = m.index + m[0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
    const body = text.slice(start, end).trim();
    if (body) blocks.push({ heading, body });
  });

  return blocks;
}

// Mirrors the PDF renderer's fallback: pull the last question-mark sentence
// out of the prose to use as the "question worth sitting with" panel.
function extractReflectionQuestion(body) {
  const sentences = body.match(/[^.!?]+[.!?]+/g) || [];
  for (let i = sentences.length - 1; i >= 0; i--) {
    const s = sentences[i].trim();
    if (s.endsWith("?")) {
      const idx = body.lastIndexOf(s);
      const rest = (body.slice(0, idx) + body.slice(idx + s.length)).trim();
      return { question: s, rest };
    }
  }
  return { question: "", rest: body };
}

function splitParagraphs(text) {
  return text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

function Shell({ session, logout, children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "3rem 1.5rem 4rem",
        background: "radial-gradient(circle at 50% 20%, #14102A 0%, #03030A 70%)",
        color: "#F4F6F0",
        fontFamily: "Outfit, system-ui, sans-serif",
        position: "relative",
      }}
    >
      <div className="mandala-watermark" />
      <div className="grain-overlay" />

      <div style={{ maxWidth: 820, margin: "0 auto", position: "relative", zIndex: 3 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2.5rem" }}>
          <Link to="/" className="subtitle-caps" style={{ textDecoration: "none" }}>
            Mind Kundli
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span style={{ fontSize: "0.85rem", color: "#A0A0B0" }}>{session?.email}</span>
            <button
              onClick={logout}
              data-testid="dashboard-logout"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                background: "none",
                border: "none",
                color: "#A0A0B0",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontFamily: "inherit",
              }}
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function LanguagePills({ current, onChange, disabled }) {
  return (
    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          onClick={() => onChange(l.code)}
          disabled={disabled}
          style={{
            padding: "0.5rem 1.1rem",
            borderRadius: 999,
            border: `1px solid ${current === l.code ? BRASS : RULE}`,
            background: current === l.code ? BRASS : "transparent",
            color: current === l.code ? IVORY : MUTED,
            fontFamily: F_SANS,
            fontSize: "0.85rem",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

// One dimension section — mirrors draw_dimension() in pdf_generator.py:
// number/name label, theme + score, wide photo, caption, prose, sage
// reflection panel with brass left rule, dark forest practice panel.
function DimensionSection({ dim, index, body }) {
  const { question, rest } = extractReflectionQuestion(body);
  const paragraphs = splitParagraphs(rest);
  const caption = (dim?.caption || "").trim() || `${dim?.label || ""}, in your own measure.`;

  return (
    <div style={{ marginBottom: "3.5rem" }}>
      <div style={{ fontFamily: F_SANS, fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase", color: BRASS, marginBottom: "0.5rem" }}>
        {String(dim?.id || index + 1).padStart(2, "0")} / {dim?.clinical_name || dim?.dimension_name}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "1.2rem" }}>
        <h3 style={{ fontFamily: F_SERIF, fontWeight: 400, fontSize: "2rem", color: FOREST, margin: 0 }}>
          {dim?.label}
        </h3>
        <div style={{ fontFamily: F_SERIF, fontSize: "1.8rem", color: FOREST }}>
          {dim?.score ?? "—"}<span style={{ fontFamily: F_SANS, fontSize: "0.7rem", color: MUTED }}> /100</span>
        </div>
      </div>

      {dim?.photo && (
        <img
          src={`/photos/${dim.photo}.jpg`}
          alt=""
          style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
        />
      )}
      <div style={{ fontFamily: F_SERIF, fontStyle: "italic", fontSize: "0.95rem", color: MUTED, margin: "0.6rem 0 1.5rem" }}>
        {caption}
      </div>

      <div style={{ fontFamily: F_SANS, fontSize: "0.95rem", lineHeight: 1.75, color: BODY_C }}>
        {paragraphs.map((p, i) => (
          <p key={i} style={{ margin: "0 0 1rem" }}>{p}</p>
        ))}
      </div>

      {question && (
        <div style={{ background: SAGE, borderLeft: `4px solid ${BRASS}`, padding: "1.1rem 1.4rem", margin: "1.5rem 0" }}>
          <div style={{ fontFamily: F_SANS, fontSize: "0.68rem", letterSpacing: "0.16em", textTransform: "uppercase", color: FOREST, marginBottom: "0.4rem" }}>
            A question worth sitting with
          </div>
          <div style={{ fontFamily: F_SERIF, fontSize: "1.05rem", color: FOREST, lineHeight: 1.5 }}>{question}</div>
        </div>
      )}

      {dim?.practice_body && (
        <div style={{ background: FOREST, padding: "1.4rem", marginTop: "1rem" }}>
          <div style={{ fontFamily: F_SANS, fontSize: "0.68rem", letterSpacing: "0.16em", textTransform: "uppercase", color: PALE_BRASS, marginBottom: "0.4rem" }}>
            {["Practice", ...(dim.practice_meta || [])].join(" / ")}
          </div>
          <div style={{ fontFamily: F_SERIF, fontSize: "1.35rem", color: IVORY, marginBottom: "0.6rem" }}>
            {dim.practice_title}
          </div>
          <div style={{ fontFamily: F_SANS, fontSize: "0.9rem", color: "#E1E8E2", lineHeight: 1.65 }}>
            {dim.practice_body}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { session, logout } = useRequireAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [lang, setLang] = useState("hinglish");
  const [translating, setTranslating] = useState(false);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [originalContent, setOriginalContent] = useState(null);
  const [langCache, setLangCache] = useState({});

  useEffect(() => {
    if (!translating) return;
    const interval = setInterval(() => {
      setQuoteIndex((i) => (i + 1) % TRANSLATE_QUOTES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [translating]);

  const load = async () => {
    try {
      const [meData, reportData] = await Promise.all([
        apiFetch("/me", { auth: true }),
        apiFetch("/report", { auth: true }),
      ]);
      setMe(meData);
      setReport(reportData);
      if (reportData?.report_text) {
        setOriginalContent({ report_text: reportData.report_text, dimension_scores: reportData.dimension_scores });
      }
    } catch (err) {
      setError(err.message || "Couldn't load your dashboard. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.session_token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // NOTE: backend endpoint for on-demand translation isn't built yet.
  // This calls /report/translate?lang= which needs to exist server-side —
  // see the note below the component for what that endpoint must do.
  const handleLanguageChange = async (newLang) => {
    if (newLang === lang || !report?.id) return;

    if (newLang === "hinglish") {
      setLang(newLang);
      if (originalContent) {
        setReport((prev) => ({ ...prev, report_text: originalContent.report_text, dimension_scores: originalContent.dimension_scores }));
      }
      return;
    }

    if (langCache[newLang]) {
      setLang(newLang);
      setReport((prev) => ({ ...prev, report_text: langCache[newLang].report_text, dimension_scores: langCache[newLang].dimension_scores }));
      return;
    }

    setLang(newLang);
    setTranslating(true);
    try {
      const data = await apiFetch(`/report/translate?lang=${newLang}`, { auth: true });
      setLangCache((prev) => ({ ...prev, [newLang]: data }));
      setReport((prev) => ({ ...prev, report_text: data.report_text, dimension_scores: data.dimension_scores || prev.dimension_scores }));
    } catch (err) {
      setError(err.message || "Couldn't prepare that language yet.");
      setLang("hinglish");
    } finally {
      setTranslating(false);
    }
  };

  const handleDownload = async () => {
    if (!report?.id || downloading) return;
    setDownloading(true);
    try {
      const res = await apiFetch(`/report/download/${report.id}?lang=${lang}`, { auth: true, raw: true });
      if (!res.ok) throw new Error("Download failed — please try again.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mind-kundli-report-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Couldn't download your PDF right now.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Shell session={session} logout={logout}>
        <div className="pulse-dot" style={{ width: 10, height: 10, borderRadius: "50%", background: "#D4AF37" }} />
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell session={session} logout={logout}>
        <p style={{ color: "#E0115F" }}>{error}</p>
      </Shell>
    );
  }

  const reportReady = report?.status === "approved" || report?.status === "sent";

  return (
    <Shell session={session} logout={logout}>
      <h1 className="serif" style={{ fontSize: "2rem", fontWeight: 500, marginBottom: "0.4rem" }}>
        Welcome{me?.name ? `, ${me.name}` : ""}
      </h1>

      {!me?.assessment_completed && (
        <div className="glass-panel" style={{ padding: "2rem", borderRadius: 20, marginTop: "1.75rem" }}>
          <p style={{ color: "#A0A0B0", lineHeight: 1.6, marginBottom: "1.5rem" }}>
            You haven't taken your 60-question assessment yet. It takes about 15–20 minutes —
            find a quiet moment and answer honestly; the reading is only as accurate as your answers.
          </p>
          <Link to="/assessment" className="cta-button">Start my assessment</Link>
        </div>
      )}

      {me?.assessment_completed && !reportReady && (
        <div className="glass-panel" style={{ padding: "2rem", borderRadius: 20, marginTop: "1.75rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.75rem" }}>
            <span className="pulse-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: "#D4AF37" }} />
            <span className="subtitle-caps">In progress</span>
          </div>
          <p style={{ color: "#A0A0B0", lineHeight: 1.6 }}>
            Your Mind Kundli is being prepared. This usually takes a little while — we'll email you
            the moment it's ready. Feel free to check back here any time.
          </p>
        </div>
      )}

      {reportReady && (
        <>
          <div style={{ marginTop: "1.75rem" }}>
            <LanguagePills current={lang} onChange={handleLanguageChange} disabled={translating} />
          </div>

          {translating && (
            <div style={{ background: IVORY, padding: "5rem 2.5rem", textAlign: "center" }}>
              <div className="pulse-dot" style={{ width: 10, height: 10, borderRadius: "50%", background: BRASS, margin: "0 auto 1.2rem" }} />
              <div style={{ fontFamily: F_SERIF, fontSize: "1.3rem", color: FOREST, marginBottom: "0.9rem" }}>
                Preparing your report in {LANGUAGES.find((l) => l.code === lang)?.label}
              </div>
              <div key={quoteIndex} style={{ fontFamily: F_SERIF, fontStyle: "italic", fontSize: "1rem", color: MUTED, maxWidth: 420, margin: "0 auto" }}>
                "{TRANSLATE_QUOTES[quoteIndex]}"
              </div>
            </div>
          )}

          {/* ── Report paper — mirrors the PDF's ivory page exactly ── */}
          {!translating && (
          <div style={{ background: IVORY, padding: "3rem 2.5rem", position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
              <span style={{ fontFamily: F_SANS, fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase", color: PALE_BRASS, background: FOREST, padding: "0.5rem 0.9rem" }}>
                Mind Kundli
              </span>
              <button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="download-report-pdf"
                style={{
                  display: "flex", alignItems: "center", gap: "0.5rem",
                  fontFamily: F_SANS, fontSize: "0.85rem", color: IVORY,
                  background: BRASS, border: "none", padding: "0.65rem 1.2rem",
                  cursor: "pointer", opacity: downloading ? 0.7 : 1,
                }}
              >
                <Download size={15} /> {downloading ? "Preparing…" : "Download PDF"}
              </button>
            </div>

            {report.archetype && (
              <h2 style={{ fontFamily: F_SERIF, fontWeight: 400, fontSize: "2.2rem", color: FOREST, margin: "0 0 0.3rem" }}>
                "{report.archetype}"
              </h2>
            )}

            {/* Score overview — mirrors draw_score_overview() */}
            <div style={{ background: FOREST, padding: "1.6rem 1.8rem", margin: "1.5rem 0 2rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem" }}>
                <span style={{ fontFamily: F_SERIF, fontSize: "2.6rem", color: IVORY }}>{report.overall_score ?? "—"}</span>
                <span style={{ fontFamily: F_SANS, fontSize: "0.8rem", color: PALE_BRASS }}>/100</span>
              </div>
              <div>
                <div style={{ fontFamily: F_SANS, fontSize: "0.68rem", letterSpacing: "0.16em", textTransform: "uppercase", color: PALE_BRASS }}>Overall mind score</div>
                <div style={{ fontFamily: F_SERIF, fontSize: "1.4rem", color: IVORY }}>{report.overall_level || ""}</div>
              </div>
            </div>

            {report.dimension_scores?.length > 0 && (
              <div style={{ marginBottom: "2.5rem" }}>
                {report.dimension_scores.map((d) => {
                  const isTop = d.score === Math.max(...report.dimension_scores.map((x) => x.score || 0));
                  return (
                    <div key={d.dimension_key} style={{ padding: "0.7rem 0", borderBottom: `1px solid ${RULE}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", marginBottom: "0.35rem" }}>
                        <span style={{ fontFamily: F_SANS, fontWeight: 700, color: FOREST }}>{d.clinical_name || d.dimension_name}</span>
                        <span style={{ fontFamily: F_SANS, fontWeight: 700, color: isTop ? BRASS : FOREST }}>{d.score ?? "—"}</span>
                      </div>
                      <div style={{ height: 5, background: RULE }}>
                        <div style={{ height: "100%", width: `${d.score || 0}%`, background: isTop ? BRASS : FOREST }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Dimension sections + opening/closing prose */}
            {(() => {
              const dimByName = Object.fromEntries(
                (report.dimension_scores || []).map((d) => [d.clinical_name || d.dimension_name, d])
              );
              const blocks = parseReportSections(report.report_text);
              return blocks.map((block, i) => {
                if (!block.heading) {
                  // opening reflection, no dimension attached
                  return (
                    <div key={i} style={{ fontFamily: F_SANS, fontSize: "0.95rem", lineHeight: 1.75, color: BODY_C, marginBottom: "2.5rem" }}>
                      {splitParagraphs(block.body).map((p, j) => <p key={j} style={{ margin: "0 0 1rem" }}>{p}</p>)}
                    </div>
                  );
                }
                if (block.heading === "Bringing It Together") {
                  return (
                    <div key={i} style={{ marginTop: "2.5rem" }}>
                      <h3 style={{ fontFamily: F_SERIF, fontWeight: 400, fontSize: "1.8rem", color: FOREST, marginBottom: "1rem" }}>
                        Bringing it together
                      </h3>
                      <div style={{ fontFamily: F_SANS, fontSize: "0.95rem", lineHeight: 1.75, color: BODY_C }}>
                        {splitParagraphs(block.body).map((p, j) => <p key={j} style={{ margin: "0 0 1rem" }}>{p}</p>)}
                      </div>
                    </div>
                  );
                }
                const dim = dimByName[block.heading];
                return <DimensionSection key={i} dim={dim} index={i} body={block.body} />;
              });
            })()}
          </div>
          )}

          <div className="glass-panel" style={{ padding: "2rem", borderRadius: 20, marginTop: "1.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.75rem" }}>
              <Sparkles size={16} style={{ color: "#D4AF37" }} />
              <span className="subtitle-caps">Bonus content</span>
            </div>
            <p style={{ color: "#A0A0B0", lineHeight: 1.6 }}>
              Videos matched to your results will appear here soon.
            </p>
          </div>
        </>
      )}
    </Shell>
  );
}
